import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Globe, BadgeCheck, Clock, Instagram, Facebook, Link2, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook };

export default function ContactPage() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  useSeo({
    title: "Contact Soonyoung — Beauty Support Across India",
    description: "Talk to Soonyoung — India's multi-brand cosmetic showroom. Customer support for orders, original beauty products, brand partnerships & store enquiries across Mumbai, Delhi, Chennai, Bengaluru & all India.",
    path: "/contact",
  });

  useEffect(() => {
    api.get("/ecom/contact").then((r) => setInfo(r.data)).catch(() => setInfo(null));
  }, []);

  const submit = async () => {
    if (!form.name.trim() || !form.email.includes("@") || !form.message.trim()) {
      toast.error("Fill your name, a valid email, and a message");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      toast.success("Message sent — we reply within one working day");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send right now — please email us directly");
    }
    setSending(false);
  };

  const field = "w-full rounded-xl border border-champagne/50 bg-pine px-4 py-3 text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none";

  return (
    <section data-testid="contact-page" className="mx-auto max-w-6xl px-4 pb-24 pt-28 sm:px-6 sm:pt-36">
      <Reveal>
        <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">We'd love to hear from you</p>
        <h1 className="mt-3 font-display text-3xl tracking-tight text-cream sm:text-5xl">
          Talk to the <span className="italic text-gold">Sanctuary</span>
        </h1>
      </Reveal>

      <div className="mt-12 grid gap-8 lg:grid-cols-2">
        <Reveal>
          <div className="h-full rounded-3xl border border-champagne/40 bg-pine p-7 sm:p-9" data-testid="contact-address-block">
            {info?.logo_url ? (
              <img src={info.logo_url} alt={info.organization_name} className="h-9 w-auto mix-blend-multiply" data-testid="contact-logo" />
            ) : (
              <p className="font-display text-2xl text-cream" data-testid="contact-logo">{info?.organization_name || "Soonyoung"}</p>
            )}
            {info?.legal_name && (
              <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.3em] text-fern">{info.legal_name}</p>
            )}

            {info?.address && (
              <div className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-sage">
                <MapPin className="mt-1 h-4 w-4 shrink-0 text-gold" />
                <div>
                  {[info.address.line1, info.address.line2, [info.address.city, info.address.state].filter(Boolean).join(", "), [info.address.pincode, info.address.country].filter(Boolean).join(" · ")].filter(Boolean).map((l, i) => (
                    <p key={i}>{l}</p>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 space-y-3 text-sm">
              {info?.phone && (
                <a data-testid="contact-phone" href={`tel:${info.phone}`} className="flex items-center gap-3 text-cream transition-colors hover:text-gold">
                  <Phone className="h-4 w-4 text-gold" /> {info.phone}
                </a>
              )}
              {info?.alternate_phone && (
                <a data-testid="contact-alt-phone" href={`tel:${info.alternate_phone}`} className="flex items-center gap-3 text-cream transition-colors hover:text-gold">
                  <Phone className="h-4 w-4 text-gold" /> {info.alternate_phone} <span className="font-mono text-[9px] uppercase tracking-widest text-fern">alt</span>
                </a>
              )}
              {info?.email && (
                <a data-testid="contact-email" href={`mailto:${info.email}`} className="flex items-center gap-3 text-cream transition-colors hover:text-gold">
                  <Mail className="h-4 w-4 text-gold" /> {info.email}
                </a>
              )}
              {info?.website && (
                <a data-testid="contact-website" href={info.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-cream transition-colors hover:text-gold">
                  <Globe className="h-4 w-4 text-gold" /> {info.website}
                </a>
              )}
            </div>

            {info?.gstin && (
              <div className="mt-6 flex items-center gap-3 border-t border-champagne/30 pt-5" data-testid="contact-gstin">
                <span className="flex items-center gap-1.5 rounded-full bg-moss px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-gold">
                  <BadgeCheck className="h-3 w-3" /> GSTIN
                </span>
                <span className="font-mono text-xs tracking-wider text-cream">{info.gstin}</span>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-3xl border border-champagne/40 bg-pine p-7 sm:p-9" data-testid="contact-form">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Write to us</p>
            <div className="mt-5 space-y-4">
              <input data-testid="contact-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={field} />
              <input data-testid="contact-form-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address" className={field} />
              <textarea data-testid="contact-form-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help? Orders, products, partnerships…" className={`${field} resize-none`} />
              <button
                data-testid="contact-form-submit"
                onClick={submit}
                disabled={sending}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-transform hover:scale-[1.02] disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send Message"} <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <Reveal>
          <div className="flex h-full flex-col gap-6">
            {info?.support_hours && (
              <div className="flex items-center gap-4 rounded-3xl border border-champagne/40 bg-moss p-6" data-testid="contact-support-hours">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold text-forest"><Clock className="h-5 w-5" /></span>
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-fern">Support hours</p>
                  <p className="mt-1 font-display text-lg text-cream">{info.support_hours}</p>
                </div>
              </div>
            )}
            {info?.socials && Object.values(info.socials).some(Boolean) && (
              <div className="rounded-3xl border border-champagne/40 bg-pine p-6" data-testid="contact-socials">
                <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-fern">Follow the ritual</p>
                <div className="mt-4 flex gap-3">
                  {Object.entries(info.socials).filter(([, v]) => v).map(([k, v]) => {
                    const Icon = SOCIAL_ICONS[k.toLowerCase()] || Link2;
                    return (
                      <a key={k} data-testid={`contact-social-${k.toLowerCase()}`} href={v} target="_blank" rel="noreferrer" aria-label={k} className="flex h-11 w-11 items-center justify-center rounded-full border border-champagne/50 text-cream transition-colors hover:border-gold hover:text-gold">
                        <Icon className="h-4 w-4" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </Reveal>
        {info?.address?.pincode && (
          <Reveal delay={0.1}>
            <div className="h-full min-h-[280px] overflow-hidden rounded-3xl border border-champagne/40" data-testid="contact-map">
              <iframe
                title="Soonyoung location"
                src={`https://maps.google.com/maps?q=${info.address.pincode}&t=&z=13&ie=UTF8&iwloc=&output=embed`}
                className="h-full min-h-[280px] w-full"
                loading="lazy"
              />
            </div>
          </Reveal>
        )}
      </div>

      {!info && (
        <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-fern" data-testid="contact-loading">
          Loading HQ contact details…
        </p>
      )}
    </section>
  );
}
